import { useMemo } from 'react';
import { Col, Row, Card, Button } from 'react-bootstrap';
import { useQuery, useMutation, NetworkStatus } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
import { validate as uuidValidate } from 'uuid';

import get from 'lodash.get';

import LinkContainer from '../components/link_container';
import {
  renderOverlay,
  renderOffline,
  renderError,
  renderDateString,
} from '../components/render_helpers';
import ReactTable from '../components/react_table/react_table';
import { toastSuccess, toastWarning, toastError } from '../lib/toast_helpers';
import {
  consignmentItemListPageQuery,
  consignmentItemDelete as consignmentItemDeleteMutation,
} from '../graphql/consignment_item_queries';
import { settingsSet } from '../store/settings_slice';
import * as updateFunctions from '../update_functions';

const ConsignmentItemList = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.user);
  const settingsMutating = useSelector((state) => state.settings.mutating);
  const settingsOnline = useSelector((state) => state.settings.online);
  const settingsTenant = useSelector((state) => state.settings.tenant);
  const [consignmentItemDelete] = useMutation(consignmentItemDeleteMutation);

  const {
    data: pageData,
    loading: pageLoading,
    error: pageError,
    refetch: pageRefetch,
    networkStatus: pageNetworkStatus,
    client: pageClient,
  } = useQuery(consignmentItemListPageQuery, {
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ consignmentItemList }) => {
      // write an individual query for every consignmentItem in the list
      updateFunctions.consignmentItemList(pageClient, consignmentItemList);
    },
  });

  const pageLoadedOrRefetching = useMemo(
    () => !pageLoading || (pageLoading && pageNetworkStatus === NetworkStatus.refetch),
    [pageLoading, pageNetworkStatus]
  );

  const consignmentItemDeleteClicked = async (e) => {
    const dataId = e.currentTarget.getAttribute('data-id');
    const consignmentItemId = uuidValidate(dataId)
      ? dataId
      : parseInt(e.currentTarget.getAttribute('data-id'), 10);
    const mutationData = {
      variables: { id: consignmentItemId },
      context: {
        serializationKey: settingsTenant,
        tracked: true,
        recordType: 'ConsignmentItemType',
        recordId: consignmentItemId,
        mutationType: 'DELETE',
      },
      update: updateFunctions.consignmentItemDelete,
    };
    mutationData.optimisticResponse = updateFunctions.optimistic(
      'consignmentItemDelete',
      mutationData
    );
    dispatch(
      settingsSet({
        mutating: true,
      })
    );
    if (settingsOnline) {
      try {
        await consignmentItemDelete(mutationData);
        toastSuccess('Consignment Item delete ok');
      } catch (err) {
        console.log(err.toString());
        toastError('Consignment Item delete failed');
      } finally {
        dispatch(
          settingsSet({
            mutating: false,
          })
        );
      }
    } else {
      consignmentItemDelete(mutationData);
      toastWarning(`Consignment Item delete ok locally. Go online to make permanent`);
      dispatch(
        settingsSet({
          mutating: false,
        })
      );
    }
  };

  const getConsignmentName = (row) => {
    const {
      consignment: {
        supplier: { name: supplierName },
        name,
      },
    } = row;
    return [supplierName, name].join('-');
  };

  const getSiteLocationName = (row) => {
    const {
      siteLocation: { name: siteLocationName },
    } = row;
    return siteLocationName;
  };

  const sortIsoString = (rowA, rowB) => {
    const isoStringA = get(rowA, ['original', 'receipt', 'createdAt']);
    const isoStringB = get(rowB, ['original', 'receipt', 'createdAt']);
    if (!isoStringA || !isoStringB) {
      if (isoStringA && !isoStringB) {
        return 1;
      }
      if (!isoStringA && isoStringB) {
        return -1;
      }
      return 0;
    }
    // eslint-disable-next-line no-nested-ternary
    return isoStringA < isoStringB ? -1 : isoStringA > isoStringB ? 1 : 0;
  };

  const getDeliveredAt = (row) => {
    const { receipt } = row;
    const { createdAt: deliveredAt } = receipt || {};
    return renderDateString(deliveredAt);
  };

  const parentColumns = [
    {
      header: 'Consignment',
      id: 'consignment',
      accessorFn: (row) => getConsignmentName(row),
      filterType: 'dropdown',
    },
    {
      header: 'Site Location',
      id: 'siteLocation',
      accessorFn: (row) => getSiteLocationName(row),
      filterType: 'dropdown',
    },
    {
      header: 'Item Ref',
      accessorKey: 'consignmentItemReference',
      enableColumnFilter: true,
      filterFn: 'includesString',
      sortingFn: 'alphanumeric',
    },
    {
      header: 'Item Num',
      accessorKey: 'consignmentItemNumber',
      enableColumnFilter: true,
      filterFn: 'includesString',
    },
    {
      header: 'Description',
      accessorKey: 'description',
      enableColumnFilter: true,
      filterFn: 'includesString',
    },
    {
      header: 'Item Type',
      accessorKey: 'consignmentItemType',
      filterType: 'dropdown',
    },
    {
      header: 'Qty',
      accessorKey: 'quantity',
      enableColumnFilter: false,
    },
    {
      header: 'Kg',
      accessorKey: 'weightKg',
      enableColumnFilter: false,
    },
    {
      header: 'Delivered At',
      id: 'deliveredAt',
      enableColumnFilter: false,
      accessorFn: (row) => getDeliveredAt(row),
      sortingFn: sortIsoString,
    },
  ];

  const renderContent = () => (
    <>
      <Row className="mt-4 mb-3">
        <Col sm={12}>
          <div className="float-none">
            <div className="float-start">
              <h1 className="h3 mb-3">Consignment Items</h1>
            </div>
            <div className="float-end">
              <Button
                className="me-2"
                variant="primary"
                onClick={() => pageRefetch()}
                disabled={!settingsOnline}
              >
                Refresh
              </Button>
              <LinkContainer to="/consignment_items/new">
                <Button variant="primary">New Consignment Item</Button>
              </LinkContainer>
            </div>
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <Card>
            <Card.Body>
              <ReactTable
                rootName="consignmentItem"
                parentColumns={parentColumns}
                data={pageData.consignmentItemList}
                doShow={false}
                doDelete={currentUser?.perms?.administrator}
                handleDelete={consignmentItemDeleteClicked}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );

  return (
    <div>
      {renderOverlay(pageLoading, settingsMutating, settingsOnline)}
      {renderOffline(settingsOnline)}
      {renderError(pageError)}
      {!pageError && pageLoadedOrRefetching && renderContent()}
    </div>
  );
};

export default ConsignmentItemList;
